import { Link } from "react-router-dom";
import { ArrowRight, User, Users, HeartHandshake, Smile } from "lucide-react";
import { FEATURES_DATA, MODES_LIST } from "@/data/features";

const MODE_EXTRAS = {
  "individual-mode": {
    icon: User,
    color: "bg-brand-purple/10",
    iconColor: "text-brand-purple",
    highlights: [
      "Full access to all 9 core features",
      "Personalised sensory profile",
      "Insights and recommendations just for you",
    ],
  },
  "parent-mode": {
    icon: Users,
    color: "bg-brand-teal/10",
    iconColor: "text-brand-teal",
    highlights: [
      "Dedicated parent dashboard",
      "Create & assign routines for your child",
      "Real-time push notifications",
    ],
  },
  "child-mode": {
    icon: Smile,
    color: "bg-brand-peach/15",
    iconColor: "text-[#D4956A]",
    highlights: [
      "Simplified, age-appropriate interface",
      "PIN-based login — no email needed",
      "Link to parent with a 6-character code",
    ],
  },
  "carer-mode": {
    icon: HeartHandshake,
    color: "bg-brand-sage/20",
    iconColor: "text-[#7EAD87]",
    highlights: [
      "Monitor individuals in your care",
      "Track emotional tone & overload risk",
      "Sensory profile overview per person",
    ],
  },
};

export default function ModesSection() {
  return (
    <section
      id="modes"
      data-testid="modes-section"
      className="py-20 md:py-32 px-6 md:px-12 lg:px-20 bg-white relative"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#E7E9EC] to-transparent" />

      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-sm uppercase tracking-widest text-brand-purple font-semibold mb-4">
            Choose your experience
          </p>
          <h2
            data-testid="modes-headline"
            className="font-heading text-3xl md:text-5xl font-bold text-[#2F3A56] leading-tight mb-4"
          >
            Four modes, one app
          </h2>
          <p className="text-base md:text-lg text-[#5A6376] max-w-2xl mx-auto leading-relaxed">
            Everyone gets the core toolkit. Choose how you'd like to use
            NeuroNest — on your own, as a parent, as a carer, or as a child.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {MODES_LIST.map((slug) => {
            const feature = FEATURES_DATA[slug];
            const extra = MODE_EXTRAS[slug];
            const Icon = extra.icon;
            const hasScreenshots = feature.screenshots.length > 0;

            return (
              <Link
                key={slug}
                to={`/features/${slug}`}
                data-testid={`mode-card-${slug}`}
                className="group bg-[#F8F9FB] rounded-3xl border border-[#E7E9EC] p-7 flex flex-col hover:shadow-[0_8px_30px_rgba(0,0,0,0.07)] transition-all duration-300 hover:-translate-y-1"
              >
                {hasScreenshots && (
                  <div className="mb-5 rounded-2xl overflow-hidden bg-white h-36 flex items-center justify-center">
                    <img
                      src={feature.screenshots[0].url}
                      alt={`${feature.title} preview`}
                      className="h-full w-auto object-contain"
                      loading="lazy"
                    />
                  </div>
                )}
                <div
                  className={`w-12 h-12 rounded-2xl ${extra.color} flex items-center justify-center mb-5`}
                >
                  <Icon
                    size={22}
                    className={extra.iconColor}
                    strokeWidth={1.5}
                  />
                </div>
                <h3 className="font-heading text-lg font-semibold text-[#2F3A56] mb-1">
                  {feature.title}
                </h3>
                <p className="text-sm text-[#5A6376] mb-5">
                  {feature.tagline}
                </p>
                <ul className="space-y-2 mb-5 mt-auto">
                  {extra.highlights.map((h) => (
                    <li
                      key={h}
                      className="flex items-start gap-2 text-xs text-[#5A6376]"
                    >
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-brand-teal flex-shrink-0" />
                      {h}
                    </li>
                  ))}
                </ul>
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-teal group-hover:gap-2.5 transition-all duration-300">
                  Learn more <ArrowRight size={14} />
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
